import { create, useStore } from 'zustand'
import { createContext, useContext } from 'react'

type BlockIconStore = {
  /** Will always return an array, even if the block isn't in the provided tree. */
  safelyGetChildrenKeys: (parentKey: string) => string[]
}

type BlockIconStoreProps = {
  getChildrenKeys: ((parentKey: string) => string[]) | undefined
}

const StoreContext = createContext<ReturnType<typeof createStore> | null>(null)

const createStore = ({ getChildrenKeys }: BlockIconStoreProps) =>
  create<BlockIconStore>()(() => ({
    safelyGetChildrenKeys: (id) => {
      try {
        return getChildrenKeys?.(id) ?? []
      } catch {
        return []
      }
    },
  }))

export const BlockIconStoreProvider = ({
  children,
  ...props
}: BlockIconStoreProps & { children: React.ReactNode }) => {
  const store = createStore(props)
  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>
}

export const useBlockIconStore = <T,>(
  selector: (state: BlockIconStore) => T
): T => {
  const store = useContext(StoreContext)
  if (!store) {
    throw new Error(
      'useBlockIconStore must be used within BlockIconStoreProvider'
    )
  }
  return useStore(store, selector)
}
